/**
 * Botão de câmera para ler QR Code e códigos de barras (EAN, Code 128).
 * Pensado para o celular e para o app instalado (PWA).
 */

import { useEffect, useId, useRef, useState } from 'react'
import { Camera, X } from 'lucide-react'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import './QrCodeScannerButton.css'

const FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E
]

export default function QrCodeScannerButton({ onScan, title = 'Ler código com a câmera', className = '' }) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState('')
  const regionId = `qr-reader-${useId().replace(/:/g, '')}`
  const onScanRef = useRef(onScan)

  useEffect(() => {
    onScanRef.current = onScan
  }, [onScan])

  useEffect(() => {
    if (!open) return undefined

    let cancelled = false
    let scanner = null

    const start = async () => {
      scanner = new Html5Qrcode(regionId, { formatsToSupport: FORMATS, verbose: false })
      try {
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 8,
            qrbox: (width, height) => {
              const size = Math.max(180, Math.floor(Math.min(width, height) * 0.72))
              return { width: size, height: size }
            }
          },
          (decoded) => {
            if (cancelled) return
            cancelled = true
            const value = String(decoded || '').trim()
            const finish = () => {
              if (value) onScanRef.current?.(value)
              setOpen(false)
            }
            scanner
              .stop()
              .catch(() => {})
              .finally(finish)
          },
          () => {}
        )
      } catch {
        if (!cancelled) {
          setError('Não foi possível abrir a câmera. Libere o acesso nas configurações do celular.')
        }
      }
    }

    start()

    return () => {
      cancelled = true
      if (!scanner) return
      const running = scanner
      running.stop().catch(() => {}).finally(() => {
        try {
          running.clear()
        } catch {
          /* leitor já encerrado */
        }
      })
    }
  }, [open, regionId])

  return (
    <>
      <button
        type="button"
        className={`qr-scan-trigger ${className}`.trim()}
        onClick={() => {
          setError('')
          setOpen(true)
        }}
        title={title}
        aria-label={title}
      >
        <Camera size={18} />
      </button>

      {open && (
        <div className="qr-scan-backdrop" role="presentation" onClick={() => setOpen(false)}>
          <div
            className="qr-scan-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${regionId}-title`}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="qr-scan-header">
              <strong id={`${regionId}-title`}>Ler código</strong>
              <button type="button" className="qr-scan-close" onClick={() => setOpen(false)} aria-label="Fechar">
                <X size={18} />
              </button>
            </header>
            <div id={regionId} className="qr-scan-region" />
            <p className="qr-scan-hint">
              {error || 'Aponte a câmera para o QR Code ou para o código de barras do produto.'}
            </p>
          </div>
        </div>
      )}
    </>
  )
}
